import { Types } from 'mongoose';

import { WORKFLOW_DATA_GET_TYPES } from '@shared/constants';

import { IBaseEntity, IConfigurableFieldConfigResponse } from '../common';
import {
  IAgent,
  ICompany,
  IContactPersons,
  ILiaisingAgent,
  IPartnerFileInfo,
} from '../partners';
import {
  ICreatedTemplate,
  InputVariableData,
  OutputStepperData,
  OutputVariableData,
} from '../view-builder';

// ------------------------------------------------------------------------------
// ---------------------- BOMS RELATED INTERFACES -------------------------------

export interface IProductExternalSystems {
  _id: Types.ObjectId | string;
  created_on: Date;
  definition: AddProductDefinitionSection;
  covers: AddProductCoversSection[];
  deductibles_and_excess: AddProductDeductiblesExcessSection;
  additional_fields: AddProductAdditionalFieldsSection[];
  templates: AddProductTemplatesSection[];
  discounts: AddProductDiscountsSection[];
  fees_and_charges: AddProductFeesAndChargesSection[];
  rates: AddProductRatesSection[];
  checklist: AddProductChecklistSection;
  commissions: AddProductCommissionsSection[];
  clauses: AddProductClausesSection[];
  payments: AddProductPaymentsSection;
  servicing: AddProductServicingSection;
  workflows: AddProductWorkflowsSection;
  floater_covers?: AddProductCoversSection[];
}

export interface AddProductPaymentsSection {
  payment: AddProductPayments;
  method: AddProductMethods;
  mode: AddProductModes;
}

export interface AddProductPayments {
  isTolerance: boolean;
  isCashBeforeCover: boolean;
  tolerance_value: string;
  tolerance_type: string;
  cashBeforeCover_warranty: string;
  cashBeforeCover_type: string;
}

export interface AddProductMethods {
  isCash: boolean;
  isCredit: boolean;
  isBankTransfer: boolean;
  isCheque: boolean;
  isPaymentGateway: boolean;
}

export interface AddProductModes {
  isMonthlyRate: boolean;
  monthlyRate: number;
  isQuarterlyRate: boolean;
  quarterlyRate: number;
  isSemiAnnuallyRate: boolean;
  semiAnnuallyRate: number;
  isAnnuallyRate: boolean;
  annuallyRate: number;
}

export interface AddProductServicingSection {
  is_after_expiration: boolean;
  is_before_expiration: boolean;
  is_cooling_off: boolean;
  is_endorsements: boolean;
  is_insured_age_limitation: boolean;
  is_reinstatement: boolean;
  is_renewable: boolean;
  cooling_off_period: string;
  cooling_off_period_type: string;
  insured_age_limitation_age: number;
  insured_age_limitation_type: string;
  reinstatement_max_period: string;
  reinstatement_max_period_type: string;
  renewable_method: string;
  notificationArray: ServicingNotificationArray[];
}

export interface ServicingNotificationArray {
  description: string;
  descriptionKeyList: AddProductKey[];
  is_email: boolean;
  is_sms: boolean;
  title: string;
  trigger: string;
  trigger_days?: number;
  trigger_type: string;
}

export interface IPartnerExternalSystems extends IBaseEntity {
  _id?: Types.ObjectId | string;
  agent?: IAgent;
  company?: ICompany;
  contact_persons?: IContactPersons[];
  documents?: IPartnerFileInfo[];
  liaising_agent: ILiaisingAgent;
  reports_to?: Types.ObjectId | string;
  created_by?: Types.ObjectId | string;
}

// ------ IMPORTANT ::: DON'T CHANGE UNLESS YOU HAVE AUTHORITY FROM BOMS SIDE

export interface AddProductDefinition {
  type: string;
  class: string;
  category: PRODUCT_CATEGORIES;
  partner: string;
  product: string;
  nature?: string;
  medical_nature?: string;
  code: string;
  description: string;
  start_date: Date;
  end_date: Date;
  geographical_eligibility: string[];
  package: boolean;
  pre_underwritten: boolean;
}

export interface AddProductContract {
  minimum_length: string;
  minimum_duration: string;
  maximum_length: string;
  maximum_duration: string;
  commencement: PRODUCT_COMMENCEMENT;
  commencement_time: string;
  expiry: PRODUCT_EXPIRY;
  expiry_time: string;
  renewal_grace_period: string;
  renewal_grace_period_duration: string;
  minimum_extension: string;
  minimum_extension_duration: string;
  maximum_extension: string;
  maximum_extension_duration: string;
  currency?: string;
  maximum_travel_duration?: number;
}

export interface AddProductCoverages {
  coverage: string;
  minimum_age: number;
  maximum_age: number;
  minimum_sum_assured: string;
  maximum_sum_assured: string;
}

export interface AddProductSubClasses {
  sub_class: string;
  coverage?: string;
  minimum_sum_assured: string;
  maximum_sum_assured: string;
  usage: string;
}

export interface AddProductPlans {
  plan: string;
  minimum_age: number;
  maximum_age: number;
}

export interface AddProductDefinitionSection {
  definition: AddProductDefinition;
  contract: AddProductContract;
  subClasses?: AddProductSubClasses[];
  coverages?: {
    familyBasisCoveragesList: AddProductCoverages[];
    perPersonBasisCoveragesList: AddProductCoverages[];
  };
  plans?: AddProductPlans[];
}

export interface AddProductCommissionsSection {
  code: string;
  apply: string;
  value: string;
  type: string;
  sub_cover: {
    cover: string;
    value: string;
    type: string;
  }[];
  fixed: boolean;
}

export interface AddProductKey {
  key: string;
  value: string;
}

export interface AddProductClause {
  code: string;
  clause: string;
  keyList: AddProductKey[];
  description: string;
  descriptionKeyList: AddProductKey[];
}

export interface AddProductClausesSection {
  subClassName: string;
  minimumSumAssured: string;
  maximumSumAssured: string;
  clauseList: AddProductClause[];
}
export interface AddProductSubCover {
  cover: string;
  value: string;
  type: string;
}

export interface AddProductFeesAndChargesSubCover extends AddProductSubCover {
  apply: string;
}
export interface AddProductFeesAndChargesSection {
  code: string;
  apply: string;
  fixed: boolean;
  type: string;
  value: string;
  sub_cover: AddProductFeesAndChargesSubCover[];
}

export interface DiscountsList {
  code: string;
  applicableCoversList: { [key: string]: boolean }[];
  value: string;
  type: string;
  isDefault: boolean;
}
export interface AddProductDiscountsSection {
  maximumSumAssured: string;
  minimumSumAssured: string;
  subClassName?: string;
  discountsList: DiscountsList[];
  plan?: string;
  coverageName?: string;
}

export interface TripDetails {
  from: number;
  to: number;
  premium: string;
}

export interface RatesTravelCoverages {
  coverage: string;
  sum_assured: string;
}

export interface AddRatesCover {
  cover: string;
  valueType: string;
  value?: string;
  excess?: string;
  maximum?: string;
  coverages?: RatesTravelCoverages[];
}

export interface RatesTravelSection {
  section: string;
  covers: AddRatesCover[];
}

export interface AddProductRatesSection {
  maximumSumAssured?: string;
  minimumSumAssured?: string;
  subClassName?: string;
  coverageName?: string;
  coverList?: RatesCoverList[];
  minimum_age?: number;
  maximum_age?: number;
  plan?: string;
  sections?: RatesTravelSection[];
  singleTrip?: TripDetails[];
  multipleTrip?: TripDetails[];
  additionalPremium?: string;
  valueType?: string;
}

interface ParameterList {
  parameter: string;
  data_type: string;
}
export interface VariableList {
  cover: string;
  data_field: string;
  external_type: string;
  lookup_value: string;
  reference_category: string;
  source: string;
  value: string;
  variable: string;
}
export interface RatesCoverList {
  code: string;
  formula: string;
  pre_underwritten_annual_premium?: string;
  parameterList: ParameterList[];
  variableList: VariableList[];
  sublimitList: AddProductSublimit[];
}

export interface AddProductAdditionalFieldsSection {
  index: number;
  formType: 'QUICK_QUOTE' | 'QUOTATION' | 'PROPOSAL';
  field: IConfigurableFieldConfigResponse;
}

interface AddProductTemplatesSection {
  title: string;
  type: 'QUICK_QUOTE' | 'QUOTATION' | 'PROPOSAL';
  content?: ICreatedTemplate[];
  inputVariablesData?: InputVariableData[];
  outputData?: {
    variableData?: OutputVariableData[];
    stepperData?: OutputStepperData[];
  };
}

interface CompulsoryExcessRangeSubClasses {
  excess: string;
  subClass: string;
}
interface CompulsoryExcessRange {
  from: number;
  subClassesList: CompulsoryExcessRangeSubClasses[];
  to: number;
}
interface VoluntaryExcessBands {
  maximum_deductible: string;
  rate: string;
  voluntary_excess: string;
}

interface VoluntaryExcessConditions {
  additional_field: string;
  bands: VoluntaryExcessBands[];
  value: string;
}
interface AddProductDeductiblesExcessSection {
  compulsory_excess_ranges: CompulsoryExcessRange[];
  is_claim_excess: boolean;
  is_compulsory_excess: boolean;
  is_voluntary_excess: boolean;
  voluntary_excess_applicable_covers: [];
  voluntary_excess_bands: VoluntaryExcessBands[];
  voluntary_excess_conditions: VoluntaryExcessConditions[];
}

export interface ProposalModelList {
  key: string;
  make: string;
  value: string;
}

export interface ChecklistSectionItemConditions {
  _id?: string;
  additional_field: string;
  comparator: string;
  value: string;
}

export interface ChecklistSectionItem {
  checklist_subType: string;
  checklist_type: string;
  conditions: ChecklistSectionItemConditions[];
  is_mandatory: boolean;
}
export interface AddProductChecklistSection {
  proposal: ChecklistSectionItem[];
  quotation: ChecklistSectionItem[];
  quick_quote: ChecklistSectionItem[];
}

// MEDICAL RELATED

export interface CoveragesList {
  key: string;
  code: string;
  coverage_type: string;
  coverage: string;
}

// ----------------------------------------

export enum PRODUCT_CATEGORIES {
  INDIVIDUAL = 'Individual',
  GROUP = 'Group',
}

export enum PRODUCT_COMMENCEMENT {
  IMMEDIATE = 'Immediate',
  NEXT_DAY = 'Next day',
}

export enum PRODUCT_EXPIRY {
  ONE_DAY_SHORT = 'One day short',
  ONE_DAY = 'On day',
}

export interface IGetProductNames {
  code: string;
  product: string;
}

export interface IProductDefinition {
  definition: {
    definition: IGetProductNames;
  };
}

export interface AddProductSection {
  section: string;
  covers: AddProductCover[];
}

export interface AddProductCoversSection {
  subClassName?: string;
  coverageName?: string;
  minimumSumAssured?: string;
  maximumSumAssured?: string;
  minimum_age?: number;
  maximum_age?: number;
  coverList?: AddProductCover[];
  plan?: string;
  sections?: AddProductSection[];
}

export interface TravelCoverage {
  coverageName: string;
  sum_assured: string;
}

export interface AddProductCover {
  code: string;
  number: number;
  clause: string;
  keyList: AddProductKey[];
  description: string;
  descriptionKeyList: AddProductKey[];
  minimum_sum_assured: string;
  maximum_sum_assured: string;
  valueType?: string;
  coverages?: TravelCoverage[];
  sublimitList: AddProductSublimit[];
  prerequisiteList: AddProductPrerequisite[];
  geographical_eligibility: string[];
  refundable: boolean;
  method: string;
  packageable: boolean;
  optional?: boolean;
  excess?: boolean;
  // for other class types
  sum_assured?: string;
  additionalCovers?: AddProductAdditionalCover[];
  valueSource?: WORKFLOW_DATA_GET_TYPES;
  excessValue?: string;
  excessValueSource?: WORKFLOW_DATA_GET_TYPES;
}

export interface AddProductAdditionalCover {
  cover: string;
}

export interface AddProductSublimit {
  sublimit: string;
  value?: string;
  type: string;
  sum_assured?: string;
  applicableTypes?: string[];
}

export interface AddProductKey {
  key: string;
  value: string;
}

export interface AddProductPrerequisite {
  prerequisite: string;
  autoAdd: boolean;
  mandatory: boolean;
}

export interface AddProductWorkflowsSection {
  newBusiness: string;
  claims: string;
  endorsements: string;
}

export enum WORKFLOW_TYPES {
  NEW_BUSINESS = 'NEW_BUSINESS',
  CLAIMS = 'CLAIMS',
  ENDORSEMENTS = 'ENDORSEMENTS',
}

export interface WorkflowConfigurations {
  quotation_request: WorkflowQuotationRequestConfig;
  quotation_evaluation: WorkflowQuotationEvaluationConfig;
  proposal_form: WorkflowProposalFormConfig;
  proposal_evaluation: WorkflowProposalEvaluationConfig;
  endorsement_criteria: WorkflowEndorsementCriteriaConfig;
  endorsement_evaluation: WorkflowEndorsementEvaluationConfig;
  claim_intimation: WorkflowClaimIntimationConfig;
  claim_handler: WorkflowClaimHandlerConfig;
}

export interface PackageProducts {
  class: Types.ObjectId | string;
  className: string;
  product: Types.ObjectId | string;
  productName?: string;
  productCode?: string;
}

export interface IPackage extends IBaseEntity {
  name: string;
  description: string;

  products: PackageProducts[];
  created_by?: Types.ObjectId | string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WorkflowClaimIntimationConfig {
  config?: any[];
}

export interface WorkflowClaimHandlerConfig {
  onsiteAssessment: WorkflowClaimHandlerOnsiteAssessmentConfig;
  salvages: WorkflowClaimHandlerSalvagesConfig;
  reportsDocuments: WorkflowClaimHandlerReportsDocumentsConfig;
  activityHistory: WorkflowClaimHandlerActivityHistoryConfig;
  decisionMaking: WorkflowClaimHandlerDecisionMakingConfig;
  externalUsers: WorkflowClaimHandlerExternalUsersConfig[];
  config?: any[];
}

export interface WorkflowClaimHandlerExternalUsersConfig {
  name: string;
  email: string;
}
export interface WorkflowClaimHandlerDecisionMakingConfig {
  isEnabled: boolean;
  holdRequest: boolean;
  rejectProcess: boolean;
  referReinsure: boolean;
  referInvestigation: boolean;
  referExternalUsers: boolean;
}

export interface WorkflowClaimHandlerActivityHistoryConfig {
  isEnabled: boolean;
  viewHistory: boolean;
}
export interface WorkflowClaimHandlerOnsiteAssessmentConfig {
  isEnabled: boolean;
  assign: boolean;
  reassign: boolean;
  addFeedbacks: boolean;
}
export interface WorkflowClaimHandlerSalvagesConfig {
  isEnabled: boolean;
  add: boolean;
  addFeedbacks: boolean;
}
export interface WorkflowClaimHandlerReportsDocumentsConfig {
  isEnabled: boolean;
  requestDocuments: boolean;
  generateReports: boolean;
}

export interface WorkflowEndorsementCriteriaConfig {
  nameChange: boolean;
  addressChange: boolean;
  productSumAssuredChange: boolean;
  sumAssuredLoadingsAdditionalCoversChange: boolean;
  clausesChange: boolean;
  config?: any[];
}

export interface WorkflowEndorsementEvaluationConfig {
  proRate: boolean;
  shortTerm: boolean;
  inspection: boolean;
  config?: any[];
}
export interface WorkflowProposalEvaluationConfig {
  coverWiseLoading: boolean;
  totalBasedLoading: boolean;
  isAddExclusion: boolean;
  config?: any[];
}

export interface WorkflowProposalFormConfig {
  creditPayment: boolean;
  nonCreditPayment: boolean;
  isPreviousClaimsDetails: boolean;
  isDocumentRequest: boolean;
  config?: any[];
}

export interface WorkflowQuotationRequestConfig {
  allowDiscountRequest: boolean;
  allowDocumentsRequest: boolean;
  documentTypes: ConfigurationDocumentType[];
  config?: any[];
}

export interface WorkflowQuotationEvaluationConfig {
  coverWiseLoading: boolean;
  totalBasedLoading: boolean;
  systemGeneratedDocuments: boolean;
  manualDocuments: boolean;
  config?: any[];
}

export interface ConfigurationDocumentType {
  name: string;
  description: string;
  maxFileSize: number;
  pdf: boolean;
  image: boolean;
  excel: boolean;
  csv: boolean;
  docx: boolean;
  isRequired: boolean;
}
